import { AudioBus } from './AudioBus';
import { assetUrl } from './assetUrl';

export type SpeakerId = 'man' | 'woman';

export type DialogueLine = {
  id: string;
  speaker: SpeakerId;
  text: string;
  triggers: string[];
  priority: number;
  cooldownGroup?: string;
  delayAfterPrev?: number;
  follows?: string;
  audio?: string;
};

export type DialogueCharacter = {
  id: SpeakerId;
  name: string;
  displayName: string;
  color: string;
  voice: string;
  personality: string;
};

export type DialogueData = {
  characters: Record<SpeakerId, DialogueCharacter>;
  story: { premise: string; goal: string };
  lines: DialogueLine[];
};

type QueueItem = { line: DialogueLine; delay: number };

/**
 * Timed / triggered dialogue with subtitle UI + pre-rendered Piper VO.
 */
export class DialogueSystem {
  private data: DialogueData | null = null;
  private root: HTMLElement;
  private nameEl: HTMLElement;
  private textEl: HTMLElement;
  private queue: QueueItem[] = [];
  private playing: DialogueLine | null = null;
  private hideAt = 0;
  private delayLeft = 0;
  private spoken = new Set<string>();
  private groupCooldown = new Map<string, number>();
  private audio = new Audio();
  private voGain = 0.85;
  private idleAccumulator = 0;
  private idleCooldown = 18;
  private lastFollow: string | null = null;
  private ready = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.nameEl = root.querySelector('.dialogue-speaker') as HTMLElement;
    this.textEl = root.querySelector('.dialogue-text') as HTMLElement;
    this.audio.preload = 'auto';
  }

  async load(): Promise<void> {
    const res = await fetch(assetUrl('data/dialogue.json'));
    this.data = (await res.json()) as DialogueData;
    this.ready = true;
  }

  isReady(): boolean {
    return this.ready;
  }

  /** Fire all lines matching a trigger (story / tutorial / moment). */
  trigger(event: string): void {
    if (!this.data) return;
    const matches = this.data.lines
      .filter((l) => l.triggers.includes(event))
      .filter((l) => !l.follows)
      .sort((a, b) => b.priority - a.priority);

    // Take the top chain for this event (primary + optional follow-ups scheduled separately)
    const primary = matches[0];
    if (!primary) return;
    if (this.spoken.has(primary.id) && (primary.cooldownGroup === 'tutorial' || primary.cooldownGroup === 'story' || primary.cooldownGroup === 'moment')) {
      // one-shot story/tutorial
      const alts = matches.filter((l) => !this.spoken.has(l.id));
      if (!alts.length) return;
      this.enqueueChain(alts[0]);
      return;
    }
    this.enqueueChain(primary);

    // Same-trigger companions with delayAfterPrev (no follows field)
    const siblings = this.data.lines
      .filter((l) => l.triggers.includes(event) && l.id !== primary.id && !l.follows)
      .sort((a, b) => b.priority - a.priority);
    for (const s of siblings) {
      if (this.spoken.has(s.id) && (s.cooldownGroup === 'tutorial' || s.cooldownGroup === 'story' || s.cooldownGroup === 'moment')) continue;
      this.enqueueChain(s);
    }
  }

  /** Soft proximity hints (respect cooldown). */
  hint(event: string): void {
    if (!this.data) return;
    const now = performance.now() / 1000;
    const line = this.data.lines
      .filter((l) => l.triggers.includes(event))
      .sort((a, b) => b.priority - a.priority)[0];
    if (!line) return;
    const group = line.cooldownGroup ?? 'hint';
    const readyAt = this.groupCooldown.get(group) ?? 0;
    if (now < readyAt) return;
    if (this.playing || this.queue.length) return;
    this.enqueueChain(line);
    this.groupCooldown.set(group, now + 28);
  }

  /** Random ambient banter while idle. */
  noteIdle(dt: number, canBanter: boolean): void {
    if (!canBanter || !this.data) return;
    if (this.playing || this.queue.length) {
      this.idleAccumulator = 0;
      return;
    }
    this.idleAccumulator += dt;
    if (this.idleAccumulator < this.idleCooldown) return;
    this.idleAccumulator = 0;
    this.idleCooldown = 22 + Math.random() * 18;

    const now = performance.now() / 1000;
    const readyAt = this.groupCooldown.get('banter') ?? 0;
    if (now < readyAt) return;

    const starters = this.data.lines.filter(
      (l) => l.triggers.includes('idle') && !l.follows && !this.queueHas(l.id),
    );
    if (!starters.length) return;
    // Prefer unheard, else any
    const unheard = starters.filter((l) => !this.spoken.has(l.id));
    const pool = unheard.length ? unheard : starters;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    this.enqueueChain(pick);
    this.groupCooldown.set('banter', now + 35);
  }

  update(dt: number): void {
    if (this.delayLeft > 0) {
      this.delayLeft -= dt;
      if (this.delayLeft > 0) return;
    }

    if (this.playing) {
      if (performance.now() / 1000 >= this.hideAt && this.audio.paused) {
        this.playing = null;
        this.root.classList.remove('visible');
        this.lastFollow = this.lastFollow; // keep for follows resolution already done
      }
      return;
    }

    if (!this.queue.length) return;
    const next = this.queue.shift()!;
    if (next.delay > 0) {
      this.delayLeft = next.delay;
      this.queue.unshift({ line: next.line, delay: 0 });
      return;
    }
    void this.speak(next.line);
  }

  private queueHas(id: string): boolean {
    return this.queue.some((q) => q.line.id === id) || this.playing?.id === id;
  }

  private enqueueChain(start: DialogueLine): void {
    if (!this.data) return;
    if (this.queueHas(start.id)) return;
    this.queue.push({ line: start, delay: start.delayAfterPrev ?? 0 });
    // Follow-up replies
    let current = start;
    const guard = new Set<string>([start.id]);
    while (this.data) {
      const follow = this.data.lines.find((l) => l.follows === current.id);
      if (!follow || guard.has(follow.id)) break;
      guard.add(follow.id);
      this.queue.push({ line: follow, delay: follow.delayAfterPrev ?? 0.35 });
      current = follow;
    }
  }

  private async speak(line: DialogueLine): Promise<void> {
    if (!this.data) return;
    this.playing = line;
    this.spoken.add(line.id);
    this.lastFollow = line.id;
    const char = this.data.characters[line.speaker];
    this.nameEl.textContent = char.displayName;
    this.nameEl.style.color = char.color;
    this.textEl.textContent = line.text;
    this.root.dataset.speaker = line.speaker;
    this.root.classList.add('visible');

    const reading = Math.max(2.8, line.text.length * 0.045);
    this.hideAt = performance.now() / 1000 + reading + 0.4;

    if (line.audio) {
      try {
        AudioBus.get().duckMusic(Math.max(2.5, line.text.length * 0.05));
        this.audio.pause();
        this.audio.src = assetUrl(line.audio);
        this.audio.volume = this.voGain;
        await this.audio.play();
        this.hideAt = performance.now() / 1000 + Math.max(reading, this.audio.duration || reading) + 0.35;
      } catch {
        // Autoplay / decode failure — subtitles still show
      }
    }
  }
}
